import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { uniqueId } from 'lodash-es';

import { NotificationService } from '@blaze-shared/platform-data-access';
import { ButtonIconComponent, TooltipDirective } from '@blaze-shared/shared-ui';
import { IconsModule } from '@blaze-shared/theming-data-access';

import { ConnectorsService } from '../services/connectors.service';

@Component({
  selector: 'blaze-webhook-id-input',
  imports: [ButtonIconComponent, IconsModule, ReactiveFormsModule, TooltipDirective],
  templateUrl: './webhook-id-input.component.html',
  styleUrl: './webhook-id-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WebhookIdInputComponent),
      multi: true,
    },
  ],
})
export class WebhookIdInputComponent implements ControlValueAccessor, AfterViewInit {
  private readonly connectorsService = inject(ConnectorsService);
  private readonly notificationService = inject(NotificationService);

  baseUrl = input.required<string>();

  readonly controlId = uniqueId('webhook-id-input-');

  webhookId = signal<string>('');
  urlCtrl = new FormControl<string>('');

  onChange?: (value?: string) => void;
  onTouched?: () => void;

  constructor() {
    effect(() => {
      this.urlCtrl.setValue(
        this.connectorsService.makeWebhookUrl(this.baseUrl(), this.webhookId()),
      );
    });

    effect(() => {
      const webhookId = this.webhookId();
      this.onChange?.(webhookId);
    });
  }

  ngAfterViewInit(): void {
    if (this.onChange && !this.webhookId()) {
      this.generateNewWebhookId();
    }
  }

  registerOnChange(fn: (value?: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value?: string): void {
    this.webhookId.set(value ?? '');
  }

  generateNewWebhookId(): void {
    const newId = crypto.randomUUID();
    this.webhookId.set(newId);
  }

  copyUrlToClipboard(): void {
    const value = this.urlCtrl.value;
    if (!value) {
      return;
    }
    navigator.clipboard.writeText(value).then(() => {
      this.notificationService.success(`Copied to clipboard`, false, 3000);
    });
  }
}
